import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Loader2, FileText, FileSpreadsheet, FileDown, ShieldCheck, XCircle, CheckCircle2,
  Bell, Inbox, CalendarClock, DatabaseBackup, Lock, Save, Search as SearchIcon,
  Palette, Upload, Image as ImageIcon, HelpCircle, BookOpen, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import {
  getCustomers, getNotifications, getSeo, downloadExport, downloadBookingsExport,
  changePassword, getSettings, saveSettings,
} from "../api";
import { T, CARD, INP, STATUS, STATUS_LABEL } from "./theme";

const btnGhost = { border: `1px solid ${T.border}`, color: T.text, background: "transparent" };
const btnGold = { background: `linear-gradient(135deg, ${T.gold}, ${T.goldHi})`, color: "#0A0A0C" };

export const H = ({ children, right }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
    <h2 className="font-display font-bold tracking-tight text-2xl md:text-3xl" style={{ color: T.text }}>{children}</h2>
    {right && <div className="flex flex-wrap gap-2">{right}</div>}
  </div>
);

// ---------------- Customers ----------------
export function CustomersPanel() {
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  useEffect(() => { getCustomers().then(setRows).catch(() => setRows([])); }, []);
  if (!rows) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" style={{ color: T.gold }} /></div>;

  const q = search.toLowerCase();
  const filtered = q ? rows.filter((c) =>
    [c.name, c.phone, c.email, c.city].some((v) => (v || "").toLowerCase().includes(q))
  ) : rows;

  return (
    <div data-testid="panel-customers">
      <H right={
        <div className="relative">
          <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: T.textFaint }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customer..."
            className="pl-9 pr-3 py-2 text-sm rounded-lg outline-none w-64" style={INP} data-testid="customer-search" />
        </div>
      }>Customers</H>

      <div className="overflow-x-auto rounded-2xl" style={CARD}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ color: T.gold, borderBottom: `1px solid ${T.border}` }}>
              {["Name", "Phone", "Email", "City", "Services", "Requests", "Last Contact", "Status"].map((h) =>
                <th key={h} className="text-left px-4 py-3 font-semibold uppercase text-[11px] tracking-widest whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map((c, i) => (
              <tr key={i} data-testid="customer-row" style={{ color: T.text, borderBottom: `1px solid ${T.border}` }}
                className="hover:bg-white/[0.02]">
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3"><a href={`tel:${c.phone}`} style={{ color: T.gold }}>{c.phone}</a></td>
                <td className="px-4 py-3" style={{ color: T.textDim }}>{c.email}</td>
                <td className="px-4 py-3" style={{ color: T.textDim }}>{c.city}</td>
                <td className="px-4 py-3 max-w-[240px] truncate" style={{ color: T.textDim }}>{(c.services || []).join(", ")}</td>
                <td className="px-4 py-3 text-center font-semibold" style={{ color: T.gold }}>{c.count}</td>
                <td className="px-4 py-3 text-xs" style={{ color: T.textDim }}>{(c.last_contact || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full"
                    style={{ background: `${STATUS[c.status] || T.textDim}22`, color: STATUS[c.status] || T.textDim }}>
                    {STATUS_LABEL[c.status] || c.status}
                  </span>
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={8} className="px-4 py-10 text-center" style={{ color: T.textFaint }}>No customers found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Notifications ----------------
export function NotificationsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { getNotifications().then(setData).catch(() => setData({ items: [] })); }, []);
  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" style={{ color: T.gold }} /></div>;
  const icon = { request: Inbox, appointment: CalendarClock, backup: DatabaseBackup, testimonial: Bell };
  return (
    <div data-testid="panel-notifications">
      <H>Notifications</H>
      <div className="space-y-2">
        {data.items.map((n, i) => {
          const Ic = icon[n.type] || Bell;
          return (
            <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
              data-testid="notification-item" className="flex items-center gap-4 rounded-xl p-4" style={CARD}>
              <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: n.emergency ? `${T.danger}22` : T.goldSoft, color: n.emergency ? T.danger : T.gold }}>
                <Ic size={17} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm" style={{ color: T.text }}>{n.title}</div>
                <div className="text-xs mt-0.5" style={{ color: T.textFaint }}>{(n.time || "").replace("T", " ").slice(0, 16)}</div>
              </div>
              {n.emergency && (
                <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full"
                  style={{ background: `${T.danger}22`, color: T.danger }}>Emergency</span>
              )}
            </motion.div>
          );
        })}
        {!data.items.length && (
          <div className="p-10 rounded-2xl text-center" style={CARD}>
            <Bell size={28} style={{ color: T.textFaint }} className="mx-auto mb-3" />
            <p style={{ color: T.textDim }}>You&apos;re all caught up.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- SEO ----------------
export function SeoPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { getSeo().then(setD).catch(() => {}); }, []);
  if (!d) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" style={{ color: T.gold }} /></div>;
  const labels = { sitemap: "Sitemap.xml", robots: "Robots.txt", meta_title: "Meta Title", meta_description: "Meta Description", og_tags: "Open Graph Tags", structured_data: "Structured Data (JSON-LD)", favicon: "Favicon", canonical: "Canonical URL" };
  const scoreColor = d.score >= 80 ? T.success : d.score >= 60 ? T.gold : T.danger;
  return (
    <div data-testid="panel-seo">
      <H>SEO Dashboard</H>
      <div className="grid md:grid-cols-3 gap-5 mb-6">
        <div className="rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden" style={CARD}>
          <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at center, ${scoreColor}, transparent 60%)` }} />
          <div className="text-6xl font-display font-black relative z-10" style={{ color: scoreColor }}>{d.score}</div>
          <div className="text-xs uppercase tracking-[0.2em] mt-1 relative z-10" style={{ color: T.textDim }}>SEO Score</div>
        </div>
        <div className="rounded-2xl p-6 md:col-span-2 grid grid-cols-2 gap-4" style={CARD}>
          {[["Indexed Pages", d.indexed_pages], ["Sitemap", d.sitemap_status], ["Broken Links", d.broken_links], ["Missing Alt Text", d.missing_alt_text], ["Page Speed", d.page_speed]].map(([k, v]) => (
            <div key={k}>
              <div className="text-xl font-bold" style={{ color: T.text }}>{v}</div>
              <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color: T.textFaint }}>{k}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl p-6" style={CARD}>
        <h3 className="font-display font-bold uppercase text-sm tracking-widest mb-4" style={{ color: T.text }}>On-page checks</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {Object.entries(d.checks).map(([k, ok]) => (
            <div key={k} className="flex items-center gap-3 text-sm" style={{ color: T.text }}>
              {ok ? <CheckCircle2 size={17} style={{ color: T.success }} /> : <XCircle size={17} style={{ color: T.danger }} />}
              {labels[k] || k}
            </div>
          ))}
        </div>
        <p className="mt-5 text-xs flex items-center gap-2" style={{ color: T.textFaint }}>
          <ShieldCheck size={13} /> Connect Google Search Console &amp; GA4 (Branding &rarr; Analytics) for live indexing &amp; traffic data.
        </p>
      </div>
    </div>
  );
}

// ---------------- Reports ----------------
export function ReportsPanel() {
  const btn = "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-transform active:scale-[0.98]";
  const Row = ({ title, desc, onExport, tid }) => (
    <div className="rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" style={CARD}>
      <div>
        <div className="font-display font-bold text-lg" style={{ color: T.text }}>{title}</div>
        <div className="text-xs mt-1" style={{ color: T.textDim }}>{desc}</div>
      </div>
      <div className="flex gap-2">
        <button onClick={() => onExport("csv")} className={btn} style={btnGhost} data-testid={`${tid}-csv`}><FileText size={14} /> CSV</button>
        <button onClick={() => onExport("xlsx")} className={btn} style={btnGhost} data-testid={`${tid}-xlsx`}><FileSpreadsheet size={14} /> Excel</button>
        <button onClick={() => onExport("pdf")} className={btn} style={btnGold} data-testid={`${tid}-pdf`}><FileDown size={14} /> PDF</button>
      </div>
    </div>
  );
  return (
    <div data-testid="panel-reports">
      <H>Business Reports</H>
      <div className="space-y-4">
        <Row title="Estimate Requests" desc="Every estimate submitted through the website." tid="rep-quotes" onExport={(k) => downloadExport(k)} />
        <Row title="Appointments" desc="All appointment requests and their statuses." tid="rep-appt" onExport={(k) => downloadBookingsExport(k)} />
      </div>
    </div>
  );
}

// ---------------- Change Password ----------------
export function ChangePasswordPanel() {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (f.next.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    if (f.next !== f.confirm) { toast.error("New passwords do not match."); return; }
    setLoading(true);
    try {
      await changePassword(f.current, f.next);
      toast.success("Password changed successfully.");
      setF({ current: "", next: "", confirm: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to change password"); }
    finally { setLoading(false); }
  };
  const Field = ({ k, label, tid }) => (
    <div>
      <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: T.textDim }}>{label}</label>
      <input type="password" data-testid={tid} value={f[k]} onChange={(e) => set(k, e.target.value)} required
        className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={INP} />
    </div>
  );
  return (
    <div data-testid="panel-changepw">
      <H>Change Password</H>
      <form onSubmit={submit} className="max-w-md rounded-2xl p-6 space-y-4" style={CARD}>
        <div className="flex items-center gap-2 mb-2" style={{ color: T.gold }}>
          <Lock size={17} /> <span className="font-semibold text-sm uppercase tracking-widest">Secure update</span>
        </div>
        <Field k="current" label="Current password" tid="cp-current" />
        <Field k="next" label="New password (min 6 chars)" tid="cp-new" />
        <Field k="confirm" label="Confirm new password" tid="cp-confirm" />
        <button type="submit" disabled={loading} data-testid="cp-submit"
          className="w-full rounded-lg py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
          style={btnGold}>
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Update password
        </button>
      </form>
    </div>
  );
}

// ---------------- Branding ----------------
const BRAND_FIELDS = [
  ["primary_color", "Primary color", "color"],
  ["secondary_color", "Secondary / accent color", "color"],
  ["logo_url", "Company logo URL", "image"],
  ["website_url", "Website URL", "text"],
  ["ga4_id", "Google Analytics 4 ID (G-XXXXX)", "text"],
  ["gsc_verification", "Google Search Console verification", "text"],
  ["recaptcha_site_key", "reCAPTCHA v3 Site Key (public)", "text"],
  ["recaptcha_secret_key", "reCAPTCHA v3 Secret Key (server)", "text"],
  ["seo_meta_title", "SEO meta title", "text"],
  ["seo_meta_description", "SEO meta description", "textarea"],
];

function toBase64(file) {
  return new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
}

export function BrandingPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { getSettings().then(setS).catch(() => {}); }, []);
  if (!s) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" style={{ color: T.gold }} /></div>;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await saveSettings(s); toast.success("Branding saved. Refresh the website to see changes."); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="panel-branding">
      <H right={
        <button onClick={save} disabled={saving} data-testid="branding-save"
          className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-sm disabled:opacity-60"
          style={btnGold}>
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save branding
        </button>
      }>Branding &amp; Identity</H>

      <div className="rounded-2xl p-6 mb-5" style={CARD}>
        <div className="flex items-center gap-2 mb-4" style={{ color: T.gold }}>
          <Palette size={17} /> <span className="text-xs font-bold uppercase tracking-widest">Public website identity</span>
        </div>
        <p className="text-sm mb-5" style={{ color: T.textDim }}>
          These values control the public tree-service website (colors, logo, SEO). The admin dashboard branding is intentionally locked and independent from these settings.
        </p>

        <div className="grid sm:grid-cols-2 gap-5">
          {BRAND_FIELDS.map(([k, label, type]) => {
            if (type === "color") {
              return (
                <div key={k}>
                  <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: T.textDim }}>{label}</label>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={s[k] || "#0E3D22"} onChange={(e) => set(k, e.target.value)}
                      data-testid={`brand-${k}`}
                      className="w-14 h-11 rounded-lg cursor-pointer" style={{ background: T.input, border: `1px solid ${T.border}` }} />
                    <input value={s[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder="#000000"
                      className="flex-1 px-3 py-2.5 rounded-lg text-sm outline-none font-mono" style={INP} />
                  </div>
                </div>
              );
            }
            if (type === "image") {
              return (
                <div key={k}>
                  <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: T.textDim }}>{label}</label>
                  <div className="flex gap-2 items-center">
                    <input value={s[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder="/logo.png or https://..."
                      className="flex-1 px-3 py-2.5 rounded-lg text-sm outline-none" style={INP} data-testid={`brand-${k}`} />
                    <label className="px-3 py-2.5 rounded-lg cursor-pointer text-sm inline-flex items-center gap-1.5" style={btnGhost}>
                      <Upload size={13} /> Upload
                      <input type="file" accept="image/*" className="hidden"
                        onChange={async (e) => { const f = e.target.files?.[0]; if (f) set(k, await toBase64(f)); }} />
                    </label>
                  </div>
                  {s[k] && <img src={s[k]} alt="preview" className="mt-2 h-14 rounded-lg object-contain" style={{ background: T.input, padding: 8 }} />}
                </div>
              );
            }
            if (type === "textarea") {
              return (
                <div key={k} className="sm:col-span-2">
                  <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: T.textDim }}>{label}</label>
                  <textarea rows={3} value={s[k] || ""} onChange={(e) => set(k, e.target.value)}
                    data-testid={`brand-${k}`} className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={INP} />
                </div>
              );
            }
            return (
              <div key={k}>
                <label className="block text-xs uppercase tracking-widest mb-1.5" style={{ color: T.textDim }}>{label}</label>
                <input value={s[k] || ""} onChange={(e) => set(k, e.target.value)}
                  data-testid={`brand-${k}`} className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={INP} />
              </div>
            );
          })}
        </div>

        <div className="mt-6 pt-5 border-t" style={{ borderColor: T.border }}>
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: T.gold }}>Social media</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {["facebook", "instagram", "google"].map((k) => (
              <div key={k}>
                <label className="block text-xs capitalize mb-1.5" style={{ color: T.textDim }}>{k} URL</label>
                <input value={s[k] || ""} onChange={(e) => set(k, e.target.value)}
                  data-testid={`brand-${k}`} className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={INP} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl p-5" style={{ ...CARD, background: T.card2 }}>
        <div className="flex items-start gap-3">
          <ImageIcon size={17} style={{ color: T.gold }} className="mt-0.5" />
          <div className="text-xs" style={{ color: T.textDim }}>
            <strong style={{ color: T.text }}>Live preview:</strong> After saving, refresh the public website to see color / logo changes.
            The admin panel branding is intentionally locked.
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Help Center ----------------
const HELP_ARTICLES = [
  {
    id: "login", title: "How to log in", icon: Lock, category: "Getting started",
    body: [
      "Open the admin URL: your-site.com/admin",
      "Enter your email and password.",
      "If you forget your password, use the Change Password page after logging in, or ask a Super Admin to reset it from Team & Roles.",
    ],
  },
  {
    id: "change-pw", title: "Changing your password", icon: Lock, category: "Getting started",
    body: [
      "Click Change Password in the sidebar (Account section).",
      "Enter your current password, then a new password (minimum 6 characters).",
      "Confirm the new password and click Update password.",
    ],
  },
  {
    id: "requests", title: "Managing estimate requests", icon: Inbox, category: "Operations",
    body: [
      "Estimate Requests is where every quote submitted from the website appears.",
      "Filter by workflow stage: New → Contacted → Scheduled → In Progress → Completed / Cancelled.",
      "Click View to see full details and the customer's request history.",
      "Change the status from the dropdown to move a request through the workflow.",
    ],
  },
  {
    id: "customers", title: "Working with customers", icon: HelpCircle, category: "Operations",
    body: [
      "Customers aggregates all people who have contacted you (matched by email or phone).",
      "Search by name, phone, email or city.",
      "Each customer card shows total requests, services requested, and last contact date.",
    ],
  },
  {
    id: "appts", title: "Appointments & Calendar", icon: CalendarClock, category: "Operations",
    body: [
      "Appointments shows every booking request.",
      "Update status (Requested → Confirmed → Completed / Cancelled) with the inline dropdown.",
      "Use the Calendar page to block off Sundays, holidays, or private dates so customers cannot book them.",
    ],
  },
  {
    id: "gallery", title: "Uploading Gallery & Before/After photos", icon: HelpCircle, category: "Content",
    body: [
      "Open Gallery (or Before / After) from the sidebar.",
      "Click Add. Paste an image URL or upload a file directly.",
      "For Gallery you can also attach a video URL (mp4). Set a poster image for the thumbnail.",
      "Set Order (lower numbers appear first) and Save.",
    ],
  },
  {
    id: "blog", title: "Publishing blog posts", icon: HelpCircle, category: "Content",
    body: [
      "Open Blog → Add.",
      "Fill in Title, URL slug, Excerpt (short preview), and Body (HTML supported).",
      "Set Meta Description for SEO. Toggle Published to make it live.",
    ],
  },
  {
    id: "testimonials", title: "Managing testimonials", icon: HelpCircle, category: "Content",
    body: [
      "Add customer reviews with their name, star rating (1-5), and the review text.",
      "Optional: Date label (e.g. 'Nov 2025') and Source (e.g. 'Google Reviews').",
    ],
  },
  {
    id: "reports", title: "Reports & exports", icon: FileDown, category: "Growth",
    body: [
      "Reports lets you export Estimate Requests and Appointments as CSV, Excel, or PDF.",
      "Great for accounting, tax season, or sharing with your team.",
    ],
  },
  {
    id: "seo", title: "SEO dashboard", icon: HelpCircle, category: "Growth",
    body: [
      "SEO shows a score based on on-page checks (title, description, structured data, sitemap...).",
      "Fix red X items to raise the score.",
      "For live traffic data, add your Google Analytics 4 ID under Branding.",
    ],
  },
  {
    id: "branding", title: "Branding & colors", icon: Palette, category: "Configuration",
    body: [
      "Open Branding to change the public website's primary color, secondary color, and logo.",
      "You can also set your Google Analytics 4 ID and Search Console verification here.",
      "Note: only admins can see this page. The admin dashboard branding is intentionally locked.",
    ],
  },
  {
    id: "backup", title: "Backup & Restore", icon: DatabaseBackup, category: "Configuration",
    body: [
      "Automatic backups run daily. Click 'Run backup now' to create a manual snapshot immediately.",
      "The last 10 snapshots are kept. Click Download to save a JSON file, or Restore to roll back.",
      "Restoring replaces current data with the snapshot's data.",
    ],
  },
  {
    id: "users", title: "Team & Roles", icon: HelpCircle, category: "Configuration",
    body: [
      "Add teammates or clients. Roles:",
      "• Super Admin — full platform control (administrator only)",
      "• Administrator — everything except role management",
      "• Editor — content editing (services, gallery, blog, testimonials)",
      "• Client — website owner. Can manage content, requests, appointments, but NOT branding, users, or backups.",
    ],
  },
];

const FAQ = [
  { q: "How do I reset a user's password?", a: "Open Team & Roles → click Edit on the user → enter a new password in the 'New password (optional)' field → Save. Ask them to change it after first login via Change Password." },
  { q: "Do I need to publish the blog post for it to appear on the website?", a: "Yes. The Published checkbox controls whether the post is visible on the public blog page. Unpublished posts stay as drafts." },
  { q: "How often does automatic backup run?", a: "Daily. The last 10 snapshots are kept automatically. You can also run a backup manually anytime from Backup & Restore." },
  { q: "Can a Client see other customers' data?", a: "Yes — the Client role can view estimate requests and customers (to respond and manage), but cannot access branding, backups, roles or SEO globals." },
  { q: "What happens if I click Restore?", a: "The system replaces current content (services, gallery, blog, requests) with the snapshot's data. Settings like your business phone and email are preserved separately." },
  { q: "Where do email notifications go?", a: "New estimate requests are emailed to the Quote destination email set under Business Info. Customer auto-confirmations also come from Business Info settings." },
];

export function HelpCenterPanel() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const filtered = useMemo(() => {
    if (!q) return HELP_ARTICLES;
    const t = q.toLowerCase();
    return HELP_ARTICLES.filter((a) =>
      a.title.toLowerCase().includes(t) || a.body.some((line) => line.toLowerCase().includes(t)) || a.category.toLowerCase().includes(t)
    );
  }, [q]);
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((a) => { (g[a.category] ??= []).push(a); });
    return g;
  }, [filtered]);

  return (
    <div data-testid="panel-help">
      <H>Help Center</H>

      <div className="relative mb-6 max-w-2xl">
        <SearchIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: T.textFaint }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="help-search"
          placeholder="Search articles, e.g. 'how do I upload a photo'"
          className="w-full pl-11 pr-4 py-3 text-sm rounded-xl outline-none" style={INP} />
      </div>

      {Object.keys(grouped).map((cat) => (
        <div key={cat} className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={14} style={{ color: T.gold }} />
            <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: T.gold }}>{cat}</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {grouped[cat].map((a) => {
              const Ic = a.icon;
              const isOpen = open === a.id;
              return (
                <div key={a.id} className="rounded-xl overflow-hidden" style={CARD}>
                  <button onClick={() => setOpen(isOpen ? null : a.id)} data-testid={`help-${a.id}`}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: T.goldSoft, color: T.gold }}><Ic size={15} /></span>
                    <span className="flex-1 text-sm font-medium" style={{ color: T.text }}>{a.title}</span>
                    <ChevronDown size={16} style={{ color: T.textFaint, transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 space-y-1.5 text-sm" style={{ color: T.textDim }}>
                      {a.body.map((line, i) => <p key={i}>{line}</p>)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {!filtered.length && (
        <div className="p-10 rounded-2xl text-center" style={CARD}>
          <HelpCircle size={30} style={{ color: T.textFaint }} className="mx-auto mb-3" />
          <p style={{ color: T.textDim }}>No articles matched &quot;{q}&quot;.</p>
        </div>
      )}

      <div className="mt-8">
        <h3 className="font-display font-bold text-xl mb-4" style={{ color: T.text }}>Frequently asked</h3>
        <div className="space-y-2">
          {FAQ.map((f, i) => (
            <details key={i} className="group rounded-xl px-4 py-3 cursor-pointer" style={CARD}>
              <summary className="flex items-center gap-3 list-none">
                <ChevronDown size={16} style={{ color: T.gold }} className="transition-transform group-open:rotate-180" />
                <span className="flex-1 text-sm font-medium" style={{ color: T.text }}>{f.q}</span>
              </summary>
              <p className="mt-3 ml-7 text-sm" style={{ color: T.textDim }}>{f.a}</p>
            </details>
          ))}
        </div>
      </div>

      <div className="mt-8 rounded-2xl p-5 flex items-start gap-3" style={{ ...CARD, background: T.card2 }}>
        <HelpCircle size={17} style={{ color: T.gold }} className="mt-0.5" />
        <div className="text-xs" style={{ color: T.textDim }}>
          <strong style={{ color: T.text }}>Need more help?</strong> Contact your website administrator and share your account email + a
          screenshot of the issue.
        </div>
      </div>
    </div>
  );
}
